/**
 * Notes HTML ↔ plain-text round-trip helpers.
 *
 * `trade.notes` (and other journal fields) can be either:
 *   - Plain text — what `tradeProcessor` writes during auto-sync
 *     ("Auto-synced from NinjaTrader\nATM: ...")
 *   - HTML — what the Quill-based RichTextEditor in AddEditTradeModal
 *     stores ("<p>...</p>" with `&nbsp;` entities for non-breaking spaces)
 *
 * The LiveTradePanel quick-edit textarea is plain text, so without these
 * helpers the user sees raw `<p>Auto-synced&nbsp;from&nbsp;...</p>` markup.
 *
 * `notesHtmlToPlain` is idempotent: passing already-plain text returns it
 * unchanged. `plainTextToNotesHtml` is round-trip safe with Quill.
 */

/**
 * Heuristic: does this string contain HTML tags or entities?
 * Conservative — only matches a known set of tag names so casual user
 * text containing `<` or `>` doesn't false-positive.
 */
export const looksLikeHtml = (s) => {
  if (!s || typeof s !== 'string') return false;
  return /<\/?(p|br|div|span|strong|em|b|i|u|ul|ol|li|h[1-6]|blockquote|code|pre)\b/i.test(s)
      || /&(nbsp|amp|lt|gt|quot|#\d+);/.test(s);
};

/**
 * Convert HTML to plain text.
 *   - Block-level closes (`</p>`, `</li>`, etc.) become `\n` so paragraph
 *     structure survives.
 *   - `<br>` becomes `\n`.
 *   - All remaining tags are stripped.
 *   - Common entities (`&nbsp;`, `&amp;`, `&lt;`, `&gt;`, `&quot;`, `&#39;`,
 *     numeric `&#NN;`) are decoded.
 *   - Runs of 3+ newlines collapse to 2 (preserves intentional blank lines).
 *   - Trailing whitespace before newlines is trimmed.
 *
 * Idempotent on plain text — short-circuits via `looksLikeHtml`.
 */
export const notesHtmlToPlain = (html) => {
  if (!html || typeof html !== 'string') return '';
  if (!looksLikeHtml(html)) return html;
  return html
    .replace(/<\/(p|div|h[1-6]|li|blockquote)>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(parseInt(n, 10)))
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
};

/**
 * Wrap plain text back as HTML matching Quill's canonical output.
 *   - Each `\n`-separated line becomes its own `<p>...</p>`.
 *   - Empty lines become `<p><br></p>` (Quill's empty-paragraph form).
 *   - `<`, `>`, `&` are escaped so user text containing them stays inert.
 */
export const plainTextToNotesHtml = (text) => {
  if (!text) return '';
  const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return text
    .split(/\n/)
    .map(line => `<p>${escape(line) || '<br>'}</p>`)
    .join('');
};

/**
 * Split auto-synced/imported notes into the auto-generated prefix lines
 * (system metadata: "Auto-synced from NinjaTrader" + "ATM: ..." + optional
 * "Commission: ...") and whatever the user added on top.
 *
 * Handles both storage shapes:
 *   - Plain text — what `tradeProcessor.buildJournalTrade` writes initially
 *   - HTML — Quill output after the user opens an auto-synced trade and
 *     edits via the Rich Text Editor (the prefix gets re-wrapped in `<p>`).
 *
 * @param {string} notes Raw notes blob (HTML or plain).
 * @returns {{ wasAutoSynced: boolean, autoPrefix: string, stripped: string }}
 *   `wasAutoSynced` — true when an auto-sync sentinel was detected.
 *   `autoPrefix`    — plain-text snapshot of the metadata lines (for tooltip).
 *   `stripped`      — notes blob with prefix removed; same shape as input
 *                     (HTML in, HTML out) so the caller can keep using
 *                     `dangerouslySetInnerHTML` without re-escaping.
 *
 * Idempotent on non-auto-synced notes — returns `{ wasAutoSynced: false,
 * autoPrefix: '', stripped: <original> }`.
 *
 * Why split at all: synced trades stamp 1–3 lines of system metadata on
 * every card. With 20 trades visible the cards become a wall of
 * "Auto-synced from NinjaTrader / ATM: X". Collapsing to a single ⓘ icon
 * (with the original lines on hover) keeps the metadata available for
 * audit without crowding the cards.
 */
export const stripAutoSyncedPrefix = (notes) => {
  if (!notes || typeof notes !== 'string') {
    return { wasAutoSynced: false, autoPrefix: '', stripped: notes || '' };
  }

  // HTML form: contiguous <p>...</p> blocks at the start matching the sentinel.
  // Greedy across one or more metadata paragraphs; stops at the first non-matching <p>.
  const htmlPrefixRe = /^\s*((?:<p>\s*(?:Auto-synced|Imported)[^<]*<\/p>)(?:\s*<p>\s*(?:ATM|Commission):[^<]*<\/p>)*)/i;
  const htmlMatch = notes.match(htmlPrefixRe);
  if (htmlMatch) {
    const prefixHtml = htmlMatch[0];
    return {
      wasAutoSynced: true,
      autoPrefix: notesHtmlToPlain(prefixHtml),
      stripped: notes.slice(prefixHtml.length).replace(/^\s+/, ''),
    };
  }

  // Plain-text form.
  const lines = notes.split('\n');
  if (/^\s*(Auto-synced|Imported)/.test(lines[0] || '')) {
    let i = 1;
    while (i < lines.length && /^\s*(ATM|Commission):/.test(lines[i])) i++;
    return {
      wasAutoSynced: true,
      autoPrefix: lines.slice(0, i).join('\n').trim(),
      stripped: lines.slice(i).join('\n').replace(/^\s+/, ''),
    };
  }

  return { wasAutoSynced: false, autoPrefix: '', stripped: notes };
};
